import React, { useEffect, useRef } from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { formatErrorsForDisplay, ErrorContext } from "../../utils/errorUtils";

export interface ErrorBannerProps {
  /** Error object with field keys and error messages */
  errors: Record<string, string>;
  /** Optional custom title for the error banner */
  title?: string;
  /** Whether to automatically scroll to the banner when it appears */
  autoScrollOnMount?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Context for error display (e.g., "invoice", "bill", "customer", "vendor") */
  context?: ErrorContext;
}

/**
 * ErrorBanner Component
 *
 * Displays validation errors in a prominent banner at the top of forms.
 * Supports field-level errors, line item errors, recurring journal errors, and global errors.
 *
 * Features:
 * - Smart auto-scrolling: scrolls only when errors increase or change (not when user is fixing)
 * - Formats field names into human-readable labels
 * - Groups line item errors by row number with "Line Item X" prefix
 * - Separate sections for general, recurring, and line item errors
 *
 * @example
 * <ErrorBanner
 *   errors={{
 *     date: "Date is required",
 *     notes: "Notes cannot exceed 500 characters",
 *     lineItem_0_account: "Account is required",
 *     recurringFrequency: "Frequency is required",
 *     balance: "Total debits must equal total credits"
 *   }}
 *   title="Please fix the following errors:"
 *   autoScrollOnMount={true}
 * />
 */
export function ErrorBanner({
  errors,
  title = "Please fix the following errors:",
  autoScrollOnMount = true,
  className = "",
  context,
}: ErrorBannerProps) {
  const bannerRef = useRef<HTMLDivElement>(null);
  const errorCount = Object.keys(errors).length;
  const prevErrorCountRef = useRef(0);
  const prevErrorKeysRef = useRef<string>("");

  // Auto-scroll to banner when NEW errors appear (not when errors are cleared)
  useEffect(() => {
    if (!autoScrollOnMount || errorCount === 0 || !bannerRef.current) {
      // Reset previous values when no errors
      if (errorCount === 0) {
        prevErrorCountRef.current = 0;
        prevErrorKeysRef.current = "";
      }
      return;
    }

    const previousCount = prevErrorCountRef.current;
    const errorCountIncreased = errorCount > previousCount;
    const firstTimeErrors = previousCount === 0 && errorCount > 0;

    // Track error keys to detect when same count but different errors
    const currentErrorKeys = Object.keys(errors).sort().join(",");
    const previousErrorKeys = prevErrorKeysRef.current;
    const errorKeysChanged = currentErrorKeys !== previousErrorKeys;
    const errorCountSame = errorCount === previousCount;

    // Scroll when:
    // 1. First time errors appear (0 → N errors)
    // 2. Error count INCREASES (3 → 5 errors, new validation found more errors)
    // 3. Error count SAME but keys CHANGED (1 → 1 but "date" → "notes")
    //
    // DON'T scroll when:
    // - Error count DECREASES (5 → 3, user is fixing errors)
    if (
      firstTimeErrors ||
      errorCountIncreased ||
      (errorCountSame && errorKeysChanged)
    ) {
      setTimeout(() => {
        bannerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }, 100);
    }

    // Update previous values
    prevErrorCountRef.current = errorCount;
    prevErrorKeysRef.current = currentErrorKeys;
  }, [errorCount, errors, autoScrollOnMount]);

  // Don't render if no errors
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }

  // Categorize errors into: line items, recurring journal, and general fields
  const lineItemErrors: Record<number, string[]> = {};
  const recurringErrors: Record<string, string> = {};
  const generalErrors: Record<string, string> = {};

  Object.entries(errors).forEach(([key, value]) => {
    const lineItemMatch = key.match(/^lineItem_(\d+)_(.+)$/);
    if (lineItemMatch) {
      const rowIndex = parseInt(lineItemMatch[1]);
      if (!lineItemErrors[rowIndex]) {
        lineItemErrors[rowIndex] = [];
      }
      lineItemErrors[rowIndex].push(value);
    } else if (key.startsWith("recurring")) {
      // Recurring journal errors
      recurringErrors[key] = value;
    } else {
      // General field errors
      generalErrors[key] = value;
    }
  });

  const formattedGeneralErrors = formatErrorsForDisplay(generalErrors, context);
  const formattedRecurringErrors = formatErrorsForDisplay(
    recurringErrors,
    context
  );
  const hasLineItemErrors = Object.keys(lineItemErrors).length > 0;
  const hasGeneralErrors = formattedGeneralErrors.length > 0;
  const hasRecurringErrors = formattedRecurringErrors.length > 0;

  return (
    <div
      ref={bannerRef}
      className={`flex w-full flex-col items-start gap-3 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-4 ${className}`}
      role="alert"
      aria-live="polite"
    >
      {/* Header */}
      <div className="flex w-full items-center gap-2">
        <FeatherAlertCircle className="h-5 w-5 text-error-600 flex-none" />
        <span className="text-body-bold font-body-bold text-error-700">
          {title}
        </span>
      </div>

      {/* General Field Errors */}
      {hasGeneralErrors && (
        <div className="flex flex-col items-start gap-2 pl-7">
          {formattedGeneralErrors.map(({ field, message }, index) => (
            <div
              key={field || `error-${index}`}
              className="flex items-start gap-2"
            >
              <span className="text-body font-body text-error-700">
                •{" "}
                {field && (
                  <>
                    <strong>{field}:</strong>{" "}
                  </>
                )}
                {message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Recurring Journal Errors */}
      {hasRecurringErrors && (
        <div className="flex flex-col items-start gap-2 pl-7">
          <span className="text-body-bold font-body-bold text-error-700">
            Recurring Journal Errors:
          </span>
          <div className="flex flex-col items-start gap-2 pl-4">
            {formattedRecurringErrors.map(({ field, message }, index) => (
              <div
                key={field || `recurring-${index}`}
                className="flex items-start gap-2"
              >
                <span className="text-body font-body text-error-700">
                  •{" "}
                  {field && (
                    <>
                      <strong>{field}:</strong>{" "}
                    </>
                  )}
                  {message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Line Item Errors */}
      {hasLineItemErrors && (
        <div className="flex flex-col items-start gap-2 pl-7">
          <span className="text-body-bold font-body-bold text-error-700">
            Line Item Errors:
          </span>
          {Object.entries(lineItemErrors)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([rowIndex, rowErrors]) => (
              <div key={rowIndex} className="flex items-start gap-2">
                <span className="text-body font-body text-error-700">
                  • <strong>Line Item {parseInt(rowIndex) + 1}:</strong>{" "}
                  {rowErrors.join(", ")}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default ErrorBanner;
